import cc from "classcat"
import numbro from "numbro"
import React, { useEffect, useState } from "react"

import styles from "../ProjectBudgetForm.module.css"

import currency, { formatCurrency } from "~/helpers/CurrencyHelper"
import { dashify } from "~/helpers/general-helpers"

import CurrencyInput from "~/common/CurrencyInput"
import Duration from "~/common/Duration"
import NumericInput from "~/common/NumericInput"
import { Delete } from "~/common/react-icons"

import { BudgetedRole, ProjectRate } from "~/forms/ProjectBudgetForm/types"

import Column from "../TableColumn"
import Row from "../TableRow"

import { calculateBudgetFromRoleMinutesAndRate } from "./project_budget_helpers"

type Props = {
  budgetedRole: BudgetedRole
  rate_card_rate: number
  default_full_time_minutes: number
  project_rate: ProjectRate
  projectId: number
  hideFinancials: boolean
  onRoleChange: (budgetedRole: BudgetedRole) => void
  removeRole: (id: number) => void
  onRatesUpdate: (roleRate: ProjectRate[]) => void
  showDayView: boolean
  canDelete?: boolean
  deleteTooltip?: string
}

const BudgetItemRow = (props: Props) => {
  const {
    budgetedRole,
    budgetedRole: { estimatedMinutes },
    rate_card_rate,
    project_rate,
    projectId,
    hideFinancials,
    onRoleChange,
    removeRole,
    onRatesUpdate,
    showDayView,
    default_full_time_minutes,
    canDelete = false,
    deleteTooltip,
  } = props

  const hoursPerDay = default_full_time_minutes / 60

  const [days, setDays] = useState(estimatedMinutes / default_full_time_minutes)

  const projectRate = project_rate?.rate ?? rate_card_rate
  const budgetFromEstimatedMinutes = numbro(
    calculateBudgetFromRoleMinutesAndRate(estimatedMinutes, projectRate),
  ).format({ thousandSeparated: true, mantissa: 0 })

  const [budgetInput, setBudgetInput] = useState(budgetFromEstimatedMinutes)

  const disableBudget = hideFinancials === true || !Number(projectRate)

  useEffect(() => {
    const rate = Number(projectRate)
    if (Number.isNaN(rate) || !projectRate) {
      return
    }

    setBudgetInput(budgetFromEstimatedMinutes)
  }, [projectRate]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Make sure if we update the budget, we also save the rate input.
    // This is important if project_rate doesn't exist yet.
    onRatesUpdate([
      {
        id: project_rate ? project_rate.id : null,
        roleId: budgetedRole.id,
        projectId: projectId,
        rate: Number(projectRate),
      },
    ])
  }, [projectRate, budgetInput]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBudgetInput(budgetFromEstimatedMinutes)
    setDays(estimatedMinutes / default_full_time_minutes)
  }, [estimatedMinutes]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleMinutes = (minutes) => {
    onRoleChange({ ...budgetedRole, estimatedMinutes: minutes })
  }

  const handleBudget = (e) => {
    const budget = Number(numbro.unformat(e.target.value))

    if (Number.isNaN(budget)) {
      return setBudgetInput(budgetInput)
    }

    const minutes = Math.round((budget / Number(projectRate)) * 60)
    onRoleChange({ ...budgetedRole, estimatedMinutes: minutes })
  }

  const formatBudgetInput = (val) => {
    if (!val) {
      return setBudgetInput("0")
    }
    if (!Number(val)) {
      return setBudgetInput(budgetInput)
    }

    const value = numbro(val).format({ thousandSeparated: true, mantissa: 0 })
    setBudgetInput(value)
  }

  const formattedRoleName = dashify(budgetedRole.name)

  return (
    <Row dataTest={`${formattedRoleName}-budget-row`}>
      <Column>{budgetedRole.name}</Column>
      {!hideFinancials && (
        <Column>
          <span data-test={`${formattedRoleName}-project-rate`}>
            {formatCurrency(
              showDayView ? projectRate * hoursPerDay : projectRate,
            )}
          </span>
        </Column>
      )}
      <Column>
        {showDayView ? (
          <NumericInput
            value={days || 0}
            mantissa={2}
            min={0}
            trimMantissa
            onValueChange={(e) => setDays(e)}
            onBlur={() =>
              handleMinutes(Math.round(days * default_full_time_minutes))
            }
            dataTest={`${formattedRoleName}-budget_days`}
          />
        ) : (
          <Duration
            minutesPerDay={estimatedMinutes || null}
            handleMinutesPerDay={handleMinutes}
            allowZero
            defaultDuration={0}
            dataTest={`${formattedRoleName}-budget_hours`}
            height={30}
            style={{
              borderColor: "var(--winter)",
            }}
          />
        )}
      </Column>
      {!hideFinancials && (
        <Column>
          <CurrencyInput
            name={`${formattedRoleName}-budget_dollar`}
            dataTest={`${formattedRoleName}-budget_dollar`}
            value={budgetInput}
            onChange={formatBudgetInput}
            onBlur={handleBudget}
            currency={currency}
            disabled={disableBudget}
            height={30}
            selectAllOnFocus
          />
        </Column>
      )}
      <div
        className={cc([
          styles.deleteIconContainer,
          !canDelete && styles.disabled,
        ])}
        onClick={() => canDelete && removeRole(budgetedRole.id)}
        data-test="budget-role-delete"
      >
        <Delete alt={deleteTooltip} color="var(--winter)" />
      </div>
    </Row>
  )
}
export default BudgetItemRow
